import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { MessageSquare, Loader2, Paperclip, Send, Clock, CheckCircle2, AlertCircle, XCircle, Building2, Filter } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const CATEGORY_LABELS = {
  technical: "Problemi Tecnici",
  billing: "Fatturazione",
  subscription: "Abbonamento",
  suggestion: "Suggerimenti",
  other: "Altro"
};

const PRIORITY_LABELS = {
  low: "Bassa",
  medium: "Media",
  high: "Alta",
  urgent: "Urgente"
};

const STATUS_LABELS = {
  open: "Aperto",
  in_progress: "In Lavorazione",
  resolved: "Risolto",
  closed: "Chiuso"
};

const STATUS_COLORS = {
  open: "bg-blue-500",
  in_progress: "bg-yellow-500",
  resolved: "bg-green-500",
  closed: "bg-gray-500"
};

const PRIORITY_COLORS = {
  low: "bg-gray-400",
  medium: "bg-blue-400",
  high: "bg-orange-500",
  urgent: "bg-red-500"
};

export default function AdminTickets() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { ticketId } = useParams();
  
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, statusFilter, priorityFilter]);

  useEffect(() => {
    if (ticketId && tickets.length > 0) {
      const ticket = tickets.find(t => t.ticket_id === ticketId);
      if (ticket) setSelectedTicket(ticket);
    }
  }, [ticketId, tickets]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (priorityFilter !== "all") params.set("priority", priorityFilter);
      
      const [ticketsRes, statsRes] = await Promise.all([
        axios.get(`${API}/tickets?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tickets/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API}/tickets/${selectedTicket.ticket_id}/messages`, {
        message: newMessage,
        attachments: attachments.map(a => a.url)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Risposta inviata");
      setNewMessage("");
      setAttachments([]);
      
      const res = await axios.get(`${API}/tickets/${selectedTicket.ticket_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(res.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/tickets/${selectedTicket.ticket_id}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Stato aggiornato");
      
      const res = await axios.get(`${API}/tickets/${selectedTicket.ticket_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(res.data);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande (max 10MB)");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await axios.post(`${API}/tickets/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setAttachments([...attachments, res.data]);
    } catch (error) {
      toast.error("Errore nel caricamento file");
    } finally {
      setUploading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "open": return <Clock className="h-4 w-4" />;
      case "in_progress": return <AlertCircle className="h-4 w-4" />;
      case "resolved": return <CheckCircle2 className="h-4 w-4" />;
      case "closed": return <XCircle className="h-4 w-4" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Gestione <span className="text-primary">Ticket</span>
          </h1>
          <p className="text-sm text-muted-foreground">Supporto alle aziende partner</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="card-dashboard text-center">
              <p className="text-2xl font-bold font-mono">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Totali</p>
            </div>
            <div className="card-dashboard text-center border-blue-500">
              <p className="text-2xl font-bold font-mono text-blue-500">{stats.open}</p>
              <p className="text-xs text-muted-foreground">Aperti</p>
            </div>
            <div className="card-dashboard text-center border-yellow-500">
              <p className="text-2xl font-bold font-mono text-yellow-500">{stats.in_progress}</p>
              <p className="text-xs text-muted-foreground">In Lavorazione</p>
            </div>
            <div className="card-dashboard text-center border-green-500">
              <p className="text-2xl font-bold font-mono text-green-500">{stats.resolved}</p>
              <p className="text-xs text-muted-foreground">Risolti</p>
            </div>
            <div className="card-dashboard text-center border-gray-500">
              <p className="text-2xl font-bold font-mono text-gray-500">{stats.closed}</p>
              <p className="text-xs text-muted-foreground">Chiusi</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-4 items-center">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {Object.entries(STATUS_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Priorità" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le priorità</SelectItem>
              {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ticket List */}
          <div className="lg:col-span-1 card-dashboard">
            <h2 className="font-bold mb-4">Ticket ({tickets.length})</h2>
            {tickets.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nessun ticket</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.ticket_id}
                    onClick={() => setSelectedTicket(ticket)}
                    className={`p-3 border cursor-pointer transition-colors ${
                      selectedTicket?.ticket_id === ticket.ticket_id 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{ticket.subject}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {ticket.company_name}
                        </p>
                      </div>
                      <Badge className={`${PRIORITY_COLORS[ticket.priority]} text-white text-xs`}>
                        {PRIORITY_LABELS[ticket.priority]}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <Badge className={`${STATUS_COLORS[ticket.status]} text-white text-xs`}>
                        {getStatusIcon(ticket.status)}
                        <span className="ml-1">{STATUS_LABELS[ticket.status]}</span>
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(ticket.updated_at), "dd/MM HH:mm", { locale: it })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ticket Detail */}
          <div className="lg:col-span-2 card-dashboard">
            {selectedTicket ? (
              <div className="flex flex-col h-full">
                {/* Ticket Header */}
                <div className="border-b border-border pb-4 mb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="font-bold text-lg">{selectedTicket.subject}</h2>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Building2 className="h-4 w-4" />
                        {selectedTicket.company_name}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Creato il {format(new Date(selectedTicket.created_at), "dd MMMM yyyy 'alle' HH:mm", { locale: it })}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Select value={selectedTicket.status} onValueChange={handleStatusChange}>
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-1">
                        <Badge variant="outline">{CATEGORY_LABELS[selectedTicket.category]}</Badge>
                        <Badge className={`${PRIORITY_COLORS[selectedTicket.priority]} text-white`}>
                          {PRIORITY_LABELS[selectedTicket.priority]}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto space-y-4 mb-4 max-h-[400px]">
                  {selectedTicket.messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`p-4 ${msg.sender_role === "admin" ? "bg-primary/10 ml-8" : "bg-muted/50 mr-8"}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-sm">{msg.sender_name}</span>
                        <Badge variant="outline" className="text-xs">
                          {msg.sender_role === "admin" ? "Tu" : "Azienda"}
                        </Badge>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {format(new Date(msg.created_at), "dd/MM/yyyy HH:mm", { locale: it })}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      {msg.attachments?.length > 0 && (
                        <div className="flex gap-2 mt-2">
                          {msg.attachments.map((att, idx) => (
                            <a
                              key={idx}
                              href={att}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                            >
                              <Paperclip className="h-3 w-3" />
                              Allegato {idx + 1}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Reply Form */}
                {selectedTicket.status !== "closed" && (
                  <div className="border-t border-border pt-4">
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Scrivi una risposta..."
                          className="min-h-[80px]"
                        />
                        {attachments.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {attachments.map((att, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {att.filename}
                                <button onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))} className="ml-1">×</button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="cursor-pointer">
                          <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} />
                          <Button variant="outline" size="icon" asChild disabled={uploading}>
                            <span>{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}</span>
                          </Button>
                        </label>
                        <Button onClick={handleSendMessage} disabled={sending || !newMessage.trim()} className="btn-primary">
                          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Seleziona un ticket per visualizzarlo</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
